import { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, Search, Edit, Trash2, X, RefreshCw, Monitor, 
  Smartphone, Server, Cloud, CheckCircle, AlertCircle, Upload, Download,
  AppWindow, Router, ServerCog, Package,
  ArrowUp, ArrowDown, ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';
import { ClickableCard, ActiveFilterChip } from '../components/ClickableCard';

const ASSET_CARDS = [
  { key: 'all', label: 'Total Assets', stat: 'total', tone: '' },
  { key: 'hardware', label: 'Hardware', stat: 'hardware', tone: 'text-slate-600', test: (a) => a.asset_type === 'Hardware' },
  { key: 'software', label: 'Software', stat: 'software', tone: 'text-slate-600', test: (a) => a.asset_type === 'Software' },
  { key: 'cloud', label: 'Cloud Assets', stat: 'cloud', tone: 'text-sky-600', test: (a) => a.asset_type === 'Cloud Asset' },
  { key: 'compliant', label: 'Compliant', stat: 'compliant', tone: 'text-green-600', test: (a) => a.compliance_status === 'Compliant' },
];

const CRITICALITY_LABELS = { Critical: 'Critical – Tier 0', High: 'High – Tier 1', Medium: 'Medium – Tier 2', Low: 'Low – Tier 3' };
const TIER_CARDS = [
  { key: 'tier0', level: 'Critical', tone: 'text-red-600' },
  { key: 'tier1', level: 'High', tone: 'text-orange-600' },
  { key: 'tier2', level: 'Medium', tone: 'text-amber-600' },
  { key: 'tier3', level: 'Low', tone: 'text-green-600' },
].map((c) => ({ ...c, label: CRITICALITY_LABELS[c.level], test: (a) => a.criticality === c.level }));

const AssetStatCard = ({ card, value, active, onClick }) => (
  <ClickableCard active={active} onClick={onClick} testid={`asset-card-${card.key}`} className="rounded-sm">
    <div className="bg-white border border-border rounded-sm p-4 h-full">
      <p className="text-sm text-slate-600">{card.label}</p>
      <p className={`text-2xl font-heading font-semibold mt-1 ${card.tone}`} data-testid={`asset-stat-${card.key}`}>{value}</p>
    </div>
  </ClickableCard>
);

export default function AssetRegisterPage() {
  const { viewingOrg } = useContext(AuthContext);
  const [assets, setAssets] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);

  // Sorting
  const [sortKey, setSortKey] = useState('asset_name');
  const [sortDir, setSortDir] = useState('asc');

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAction, setBulkAction] = useState('');  // 'status'|'assigned_to'|'department'|'category'
  const [bulkValue, setBulkValue] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  
  const [cardKey, setCardKey] = useState('all');
  const activeCard = [...ASSET_CARDS, ...TIER_CARDS].find((c) => c.key === cardKey);
  const pickCard = (key) => setCardKey(cardKey === key ? 'all' : key);
  const [filters, setFilters] = useState({
    search: '',
    assetType: 'all',
    status: 'all'
  });
  
  const [formData, setFormData] = useState({
    asset_id: '',
    asset_name: '',
    asset_type: 'Hardware',
    category: '',
    manufacturer: '',
    model: '',
    serial_number: '',
    operating_system: '',
    os_version: '',
    assigned_to: '',
    department: '',
    location: '',
    purchase_date: '',
    warranty_expiry: '',
    criticality: 'Medium',
    status: 'Active',
    compliance_status: 'Not Assessed',
    is_encrypted: false,
    tags: '',
    notes: ''
  });

  useEffect(() => {
    fetchAssets();
  }, [refreshKey, viewingOrg]);

  useEffect(() => {
    applyFilters();
  }, [assets, filters, cardKey]);

  const getViewParam = () => viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';

  const fetchAssets = async () => {
    try {
      setLoading(true);
      const param = viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';
      const response = await axios.get(`${API}/assets${param}`);
      setAssets(response.data);
      setFilteredAssets(response.data);
    } catch (error) {
      toast.error('Failed to fetch assets');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = () => {
    setRefreshKey(prev => prev + 1);
    setSelectedIds([]);
  };

  // Sorted view of the filtered assets.
  const sortedAssets = useMemo(() => {
    const arr = [...filteredAssets];
    const key = sortKey;
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let av = key === 'asset_name' ? (a.name || a.asset_name || '') : (a[key] || '');
      let bv = key === 'asset_name' ? (b.name || b.asset_name || '') : (b[key] || '');
      // Dates compared as strings in ISO lay naturally sorted; numeric-looking
      // strings handled via localeCompare numeric option.
      return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * dir;
    });
    return arr;
  }, [filteredAssets, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="w-3 h-3 inline ml-1 opacity-40" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 inline ml-1" />
      : <ArrowDown className="w-3 h-3 inline ml-1" />;
  };

  // --- Bulk selection ---
  const visibleIds = sortedAssets.map(a => a.id).filter(Boolean);
  const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.includes(id));
  const someSelected = selectedIds.length > 0 && !allSelected;
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : visibleIds);
  };
  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const runBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} asset${selectedIds.length === 1 ? '' : 's'}? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      let ok = 0, fail = 0;
      for (const id of selectedIds) {
        try {
          await axios.delete(`${API}/assets/${id}${getViewParam()}`);
          ok++;
        } catch { fail++; }
      }
      toast.success(`Deleted ${ok}${fail ? `, failed ${fail}` : ''}`);
      setSelectedIds([]);
      refreshData();
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulkUpdate = async () => {
    if (!bulkAction) return toast.error('Pick an action');
    if (bulkValue === '' || bulkValue == null) return toast.error('Enter a value');
    if (selectedIds.length === 0) return toast.error('Select at least one asset');
    setBulkBusy(true);
    try {
      const payload = { [bulkAction]: bulkValue };
      let ok = 0, fail = 0;
      for (const id of selectedIds) {
        try {
          await axios.patch(`${API}/assets/${id}${getViewParam()}`, payload);
          ok++;
        } catch { fail++; }
      }
      toast.success(`Updated ${ok}${fail ? `, failed ${fail}` : ''}`);
      setBulkAction(''); setBulkValue('');
      setSelectedIds([]);
      refreshData();
    } finally {
      setBulkBusy(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...assets];
    
    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(asset => 
        (asset.name || asset.asset_name || '').toLowerCase().includes(search) ||
        asset.asset_id?.toLowerCase().includes(search) ||
        asset.assigned_to?.toLowerCase().includes(search) ||
        asset.serial_number?.toLowerCase().includes(search)
      );
    }
    
    if (filters.assetType !== 'all') {
      filtered = filtered.filter(asset => asset.asset_type === filters.assetType);
    }
    
    if (filters.status !== 'all') {
      filtered = filtered.filter(asset => asset.status === filters.status);
    }

    if (activeCard?.test) {
      filtered = filtered.filter(activeCard.test);
    }
    
    setFilteredAssets(filtered);
  };

  const handleSyncM365 = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/assets/sync-m365`);
      toast.success(`Synced ${response.data.total_synced} assets from M365!`);
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync assets from M365');
    } finally {
      setSyncing(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/assets/template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'asset_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleImportExcel = async () => {
    if (!importFile) {
      toast.error('Please select a file to import');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await axios.post(`${API}/assets/import-excel${getViewParam()}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResult({
        success: true,
        message: response.data.message,
        imported_count: response.data.imported_count,
        error_count: response.data.error_count,
        errors: response.data.errors
      });

      toast.success(response.data.message);
      fetchAssets();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to import Excel file';
      setImportResult({
        success: false,
        message: errorMessage
      });
      toast.error(errorMessage);
    } finally {
      setImporting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        name: formData.asset_name,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : []
      };
      delete payload.asset_name;
      
      await axios.post(`${API}/assets${getViewParam()}`, payload);
      toast.success('Asset created successfully');
      setShowAddModal(false);
      resetForm();
      refreshData();
    } catch (error) {
      toast.error('Failed to create asset');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        name: formData.asset_name,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : []
      };
      delete payload.asset_name;
      
      await axios.patch(`${API}/assets/${editingAsset.id}${getViewParam()}`, payload);
      toast.success('Asset updated successfully');
      setShowEditModal(false);
      setEditingAsset(null);
      resetForm();
      refreshData();
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update asset');
    }
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/assets/${assetId}${getViewParam()}`);
      toast.success('Asset deleted successfully');
      refreshData();
    } catch (error) {
      toast.error('Failed to delete asset');
    }
  };

  const openEditModal = (asset) => {
    setEditingAsset(asset);
    setFormData({
      asset_id: asset.asset_id,
      asset_name: asset.name || asset.asset_name || '',
      asset_type: asset.asset_type,
      category: asset.category,
      manufacturer: asset.manufacturer || '',
      model: asset.model || '',
      serial_number: asset.serial_number || '',
      operating_system: asset.operating_system || '',
      os_version: asset.os_version || '',
      assigned_to: asset.assigned_to || '',
      department: asset.department || '',
      location: asset.location || '',
      purchase_date: (asset.purchase_date || '').slice(0, 10),
      warranty_expiry: asset.warranty_expiry || '',
      criticality: asset.criticality,
      status: asset.status,
      compliance_status: asset.compliance_status,
      is_encrypted: asset.is_encrypted || false,
      tags: asset.tags ? asset.tags.join(', ') : '',
      notes: asset.notes || ''
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      asset_id: '',
      asset_name: '',
      asset_type: 'Hardware',
      category: '',
      manufacturer: '',
      model: '',
      serial_number: '',
      operating_system: '',
      os_version: '',
      assigned_to: '',
      department: '',
      location: '',
      purchase_date: '',
      warranty_expiry: '',
      criticality: 'Medium',
      status: 'Active',
      compliance_status: 'Not Assessed',
      is_encrypted: false,
      tags: '',
      notes: ''
    });
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/assets/export${getViewParam()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `asset_register_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Asset register exported');
    } catch (error) {
      toast.error('Failed to export assets');
    }
  };

  const getAssetIcon = (assetType, category) => {
    const iconClass = "w-5 h-5";
    if (assetType === 'Hardware') {
      if (category?.includes('Mobile') || category?.includes('Phone')) return <Smartphone className={iconClass} />;
      if (category?.includes('Server')) return <Server className={iconClass} />;
      return <Monitor className={iconClass} />;
    }
    if (assetType === 'Software') return <AppWindow className={iconClass} />;
    if (assetType === 'Network Device') return <Router className={iconClass} />;
    if (assetType === 'Virtual Machine') return <ServerCog className={iconClass} />;
    if (assetType === 'Cloud Asset') return <Cloud className={`${iconClass} text-sky-600`} />;
    return <Package className={iconClass} />;
  };

  const getStatusColor = (status) => {
    const colors = {
      'Active': 'bg-slate-100 text-green-800',
      'Inactive': 'bg-gray-100 text-gray-800',
      'Maintenance': 'bg-slate-100 text-slate-800',
      'Retired': 'bg-slate-100 text-red-800',
      'Non-Compliant': 'bg-slate-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getCriticalityColor = (criticality) => {
    const colors = {
      'Critical': 'bg-slate-100 text-red-800',
      'High': 'bg-slate-100 text-slate-800',
      'Medium': 'bg-slate-100 text-slate-800',
      'Low': 'bg-slate-100 text-green-800'
    };
    return colors[criticality] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg text-slate-600">Loading assets...</div>
      </div>
    );
  }

  const stats = {
    total: assets.length,
    hardware: assets.filter(a => a.asset_type === 'Hardware').length,
    software: assets.filter(a => a.asset_type === 'Software').length,
    cloud: assets.filter(a => a.asset_type === 'Cloud Asset').length,
    critical: assets.filter(a => a.criticality === 'Critical').length,
    compliant: assets.filter(a => a.compliance_status === 'Compliant').length
  };

  return (
    <div className="space-y-6" data-testid="asset-register-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight text-slate-900">
            Asset Register
          </h1>
          <p className="text-slate-600 mt-2">Manage hardware, software, and cloud assets</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={handleSyncM365}
            disabled={syncing}
            variant="outline"
            className="rounded-sm"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync from M365'}
          </Button>
          <Button
            onClick={() => setShowImportModal(true)}
            variant="outline"
            className="rounded-sm"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import Excel
          </Button>
          <Button onClick={handleExport} variant="outline" className="rounded-sm" data-testid="export-assets-btn">
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Asset
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {ASSET_CARDS.map((c) => (
          <AssetStatCard key={c.key} card={c} value={stats[c.stat]} active={cardKey === c.key} onClick={() => pickCard(c.key)} />
        ))}
      </div>
      <div>
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Criticality</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="asset-criticality-cards">
          {TIER_CARDS.map((c) => (
            <AssetStatCard key={c.key} card={c} value={assets.filter(c.test).length} active={cardKey === c.key} onClick={() => pickCard(c.key)} />
          ))}
        </div>
      </div>

      {activeCard?.test && (
        <ActiveFilterChip label={`${activeCard.label} (${filteredAssets.length})`} onClear={() => setCardKey('all')} testid="asset-active-filter" />
      )}

      {/* Filters */}
      <div className="bg-white border border-border rounded-sm p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-600" />
              <Input
                placeholder="Search assets..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10 rounded-sm"
              />
            </div>
          </div>
          <select
            value={filters.assetType}
            onChange={(e) => setFilters({ ...filters, assetType: e.target.value })}
            className="rounded-sm border border-slate-700 bg-white text-slate-900 px-4 py-2 text-sm"
          >
            <option value="all">All Types</option>
            <option value="Hardware">Hardware</option>
            <option value="Software">Software</option>
            <option value="Network Device">Network Device</option>
            <option value="Virtual Machine">Virtual Machine</option>
            <option value="Cloud Asset">Cloud Asset</option>
            <option value="Other">Other</option>
          </select>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="rounded-sm border border-slate-700 bg-white text-slate-900 px-4 py-2 text-sm"
          >
            <option value="all">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Maintenance">Maintenance</option>
            <option value="Retired">Retired</option>
          </select>
        </div>
      </div>

      {/* Bulk action bar — appears only when assets are selected */}
      {selectedIds.length > 0 && (
        <div className="bg-slate-900 text-white rounded-sm px-4 py-2 flex items-center flex-wrap gap-2" data-testid="bulk-action-bar">
          <span className="text-sm font-medium">{selectedIds.length} selected</span>
          <span className="h-4 w-px bg-slate-600 mx-1" />
          <select
            value={bulkAction}
            onChange={(e) => { setBulkAction(e.target.value); setBulkValue(''); }}
            className="rounded-sm px-2 py-1 text-sm text-slate-900"
            data-testid="bulk-action-select"
          >
            <option value="">Change…</option>
            <option value="status">Status</option>
            <option value="assigned_to">Assigned To</option>
            <option value="department">Department</option>
            <option value="category">Category</option>
          </select>
          {bulkAction === 'status' ? (
            <select
              value={bulkValue}
              onChange={(e) => setBulkValue(e.target.value)}
              className="rounded-sm px-2 py-1 text-sm text-slate-900"
              data-testid="bulk-value"
            >
              <option value="">—</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Maintenance">Maintenance</option>
              <option value="Retired">Retired</option>
            </select>
          ) : bulkAction ? (
            <input
              type="text"
              value={bulkValue}
              onChange={(e) => setBulkValue(e.target.value)}
              placeholder={bulkAction === 'assigned_to' ? 'Email / name' : bulkAction === 'department' ? 'Department' : 'Category'}
              className="rounded-sm px-2 py-1 text-sm text-slate-900 min-w-[180px]"
              data-testid="bulk-value"
            />
          ) : null}
          <Button
            size="sm"
            onClick={runBulkUpdate}
            disabled={!bulkAction || bulkValue === '' || bulkBusy}
            className="bg-white text-slate-900 hover:bg-slate-200"
            data-testid="bulk-apply"
          >
            Apply
          </Button>
          <span className="h-4 w-px bg-slate-600 mx-1" />
          <Button
            size="sm"
            onClick={runBulkDelete}
            disabled={bulkBusy}
            className="bg-red-600 text-white hover:bg-red-500"
            data-testid="bulk-delete"
          >
            <Trash2 className="w-3 h-3 mr-1" /> Delete
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setSelectedIds([])}
            className="text-slate-900"
            data-testid="bulk-clear"
          >
            Clear
          </Button>
        </div>
      )}

      {/* Assets Table */}
      <div className="bg-white border border-border rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-border">
              <tr>
                <th className="px-3 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleSelectAll}
                    aria-label="Select all"
                    className="h-4 w-4 cursor-pointer"
                    data-testid="select-all"
                  />
                </th>
                <th
                  onClick={() => toggleSort('asset_name')}
                  className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider cursor-pointer select-none hover:bg-slate-100"
                  data-testid="sort-asset-name"
                >
                  Asset Name <SortIcon col="asset_name" />
                </th>
                <th
                  onClick={() => toggleSort('asset_id')}
                  className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider cursor-pointer select-none hover:bg-slate-100"
                  data-testid="sort-asset-id"
                >
                  Asset ID <SortIcon col="asset_id" />
                </th>
                <th
                  onClick={() => toggleSort('asset_type')}
                  className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider cursor-pointer select-none hover:bg-slate-100"
                  data-testid="sort-type"
                >
                  Type / Category <SortIcon col="asset_type" />
                </th>
                <th
                  onClick={() => toggleSort('assigned_to')}
                  className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider cursor-pointer select-none hover:bg-slate-100"
                  data-testid="sort-assigned-to"
                >
                  Assigned To <SortIcon col="assigned_to" />
                </th>
                <th
                  onClick={() => toggleSort('department')}
                  className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider cursor-pointer select-none hover:bg-slate-100"
                  data-testid="sort-department"
                >
                  Department <SortIcon col="department" />
                </th>
                <th
                  onClick={() => toggleSort('location')}
                  className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider cursor-pointer select-none hover:bg-slate-100"
                  data-testid="sort-location"
                >
                  Location <SortIcon col="location" />
                </th>
                <th
                  onClick={() => toggleSort('status')}
                  className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider cursor-pointer select-none hover:bg-slate-100"
                  data-testid="sort-status"
                >
                  Status <SortIcon col="status" />
                </th>
                <th
                  onClick={() => toggleSort('criticality')}
                  className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider cursor-pointer select-none hover:bg-slate-100"
                >
                  Criticality <SortIcon col="criticality" />
                </th>
                <th
                  onClick={() => toggleSort('purchase_date')}
                  className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider cursor-pointer select-none hover:bg-slate-100"
                  data-testid="sort-purchase-date"
                >
                  Purchase Date <SortIcon col="purchase_date" />
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sortedAssets.map((asset) => (
                <tr key={asset.id || asset.asset_id} className={`hover:bg-slate-50 ${selectedIds.includes(asset.id) ? 'bg-blue-50/50' : ''}`}>
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(asset.id)}
                      onChange={() => toggleSelect(asset.id)}
                      aria-label={`Select ${asset.name || asset.asset_name}`}
                      className="h-4 w-4 cursor-pointer"
                      data-testid={`select-${asset.asset_id}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {getAssetIcon(asset.asset_type, asset.category)}
                      <div>
                        <p className="text-sm font-medium text-slate-900">{asset.name || asset.asset_name}</p>
                        {asset.m365_synced && (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-600 mt-1">
                            <Cloud className="w-3 h-3" />
                            M365
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-xs text-slate-700 font-mono">{asset.asset_id}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-slate-900">{asset.asset_type}</p>
                    <p className="text-xs text-slate-600">{asset.category}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-slate-700">{asset.assigned_to || '-'}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-slate-700">{asset.department || '-'}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-slate-700">{asset.location || '-'}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-sm ${getStatusColor(asset.status)}`}>
                      {asset.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-sm ${getCriticalityColor(asset.criticality)}`}>
                      {CRITICALITY_LABELS[asset.criticality] || asset.criticality}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-xs text-slate-700 whitespace-nowrap">{(asset.purchase_date || '').slice(0, 10) || '-'}</p>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEditModal(asset)}
                        className="text-slate-600 hover:text-slate-900"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(asset.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {sortedAssets.length === 0 && (
          <div className="text-center py-12">
            <p className="text-slate-600">No assets found</p>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white text-slate-900 rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-xl font-heading font-semibold text-slate-900">
                {showAddModal ? 'Add New Asset' : 'Edit Asset'}
              </h2>
              <button onClick={() => { setShowAddModal(false); setShowEditModal(false); resetForm(); }}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={showAddModal ? handleSubmit : handleUpdate} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="asset_id" className="text-slate-900">Asset ID *</Label>
                  <Input
                    id="asset_id"
                    className="!bg-white !text-slate-900 border-slate-700"
                    value={formData.asset_id}
                    onChange={(e) => setFormData({ ...formData, asset_id: e.target.value })}
                    placeholder="ASSET-001"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="asset_name" className="text-slate-900">Asset Name *</Label>
                  <Input
                    id="asset_name"
                    className="!bg-white !text-slate-900 border-slate-700"
                    value={formData.asset_name}
                    onChange={(e) => setFormData({ ...formData, asset_name: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="asset_type" className="text-slate-900">Asset Type *</Label>
                  <select
                    id="asset_type"
                    value={formData.asset_type}
                    onChange={(e) => setFormData({ ...formData, asset_type: e.target.value })}
                    className="w-full rounded-sm border border-slate-700 bg-white text-slate-900 px-3 py-2 text-sm"
                    required
                  >
                    <option value="Hardware">Hardware</option>
                    <option value="Software">Software</option>
                    <option value="Network Device">Network Device</option>
                    <option value="Virtual Machine">Virtual Machine</option>
                    <option value="Cloud Asset">Cloud Asset</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="category" className="text-slate-900">Category *</Label>
                  <Input
                    id="category"
                    className="!bg-white !text-slate-900 border-slate-700"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="Laptop, Desktop, Server, etc."
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="manufacturer" className="text-slate-900">Manufacturer</Label>
                  <Input
                    id="manufacturer"
                    className="!bg-white !text-slate-900 border-slate-700"
                    value={formData.manufacturer}
                    onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="model" className="text-slate-900">Model</Label>
                  <Input
                    id="model"
                    className="!bg-white !text-slate-900 border-slate-700"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="serial_number" className="text-slate-900">Serial Number</Label>
                  <Input
                    id="serial_number"
                    className="!bg-white !text-slate-900 border-slate-700"
                    value={formData.serial_number}
                    onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="assigned_to" className="text-slate-900">Assigned To</Label>
                  <Input
                    id="assigned_to"
                    className="!bg-white !text-slate-900 border-slate-700"
                    value={formData.assigned_to}
                    onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                    placeholder="user@company.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="department" className="text-slate-900">Department</Label>
                  <Input id="department" className="!bg-white !text-slate-900 border-slate-700" value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })} placeholder="e.g. Finance" data-testid="asset-form-department" />
                </div>
                <div>
                  <Label htmlFor="location" className="text-slate-900">Location</Label>
                  <Input id="location" className="!bg-white !text-slate-900 border-slate-700" value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="e.g. Sydney office" data-testid="asset-form-location" />
                </div>
                <div>
                  <Label htmlFor="purchase_date" className="text-slate-900">Purchase Date</Label>
                  <Input id="purchase_date" type="date" className="!bg-white !text-slate-900 border-slate-700" value={formData.purchase_date}
                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })} data-testid="asset-form-purchase-date" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="operating_system" className="text-slate-900">Operating System</Label>
                  <Input
                    id="operating_system"
                    className="!bg-white !text-slate-900 border-slate-700"
                    value={formData.operating_system}
                    onChange={(e) => setFormData({ ...formData, operating_system: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="os_version" className="text-slate-900">OS Version</Label>
                  <Input
                    id="os_version"
                    className="!bg-white !text-slate-900 border-slate-700"
                    value={formData.os_version}
                    onChange={(e) => setFormData({ ...formData, os_version: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="criticality" className="text-slate-900">Criticality *</Label>
                  <select
                    id="criticality"
                    value={formData.criticality}
                    onChange={(e) => setFormData({ ...formData, criticality: e.target.value })}
                    className="w-full rounded-sm border border-slate-700 bg-white text-slate-900 px-3 py-2 text-sm"
                    required
                  >
                    {['Low', 'Medium', 'High', 'Critical'].map((lvl) => (
                      <option key={lvl} value={lvl}>{CRITICALITY_LABELS[lvl]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="status" className="text-slate-900">Status *</Label>
                  <select
                    id="status"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full rounded-sm border border-slate-700 bg-white text-slate-900 px-3 py-2 text-sm"
                    required
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Maintenance">Maintenance</option>
                    <option value="Retired">Retired</option>
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="tags" className="text-slate-900">Tags (comma-separated)</Label>
                <Input
                  id="tags"
                  className="!bg-white !text-slate-900 border-slate-700"
                    value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="laptop, windows, corporate"
                />
              </div>

              <div>
                <Label htmlFor="notes" className="text-slate-900">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm">
                  {showAddModal ? 'Create Asset' : 'Update Asset'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowAddModal(false); setShowEditModal(false); resetForm(); }}
                  className="rounded-sm"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Excel Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h2 className="text-xl font-heading font-semibold text-slate-900">Import Assets from Excel</h2>
              <button onClick={() => { setShowImportModal(false); setImportFile(null); setImportResult(null); }}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Instructions */}
              <div className="p-4 bg-slate-50 border border-slate-700 rounded-sm">
                <p className="text-sm text-slate-900 font-medium mb-2">How to import assets:</p>
                <ol className="list-decimal ml-4 space-y-1 text-sm text-slate-900">
                  <li>Download the Excel template by clicking the button below</li>
                  <li>Fill in your asset information in the template</li>
                  <li>Upload the completed Excel file</li>
                  <li>Assets will be imported or updated based on Asset ID</li>
                </ol>
              </div>

              {/* Download Template Button */}
              <Button
                onClick={handleDownloadTemplate}
                variant="outline"
                className="w-full rounded-sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Excel Template
              </Button>

              {/* File Upload */}
              <div>
                <Label htmlFor="excel-file" className="text-slate-900">Select Excel File</Label>
                <Input
                  id="excel-file"
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => setImportFile(e.target.files[0])}
                  className="mt-1"
                />
                {importFile && (
                  <p className="text-sm text-slate-600 mt-2">
                    Selected: {importFile.name}
                  </p>
                )}
              </div>

              {/* Import Result */}
              {importResult && (
                <div className={`p-4 border rounded-sm ${
                  importResult.success ? 'bg-slate-50 border-slate-700' : 'bg-slate-50 border-slate-700'
                }`}>
                  <p className={`text-sm font-medium ${
                    importResult.success ? 'text-green-900' : 'text-red-900'
                  }`}>
                    {importResult.message}
                  </p>
                  
                  {importResult.success && (
                    <div className="mt-2 text-sm text-green-900">
                      <p>✅ Imported: {importResult.imported_count} assets</p>
                      {importResult.error_count > 0 && (
                        <p className="text-slate-900">⚠️ Errors: {importResult.error_count}</p>
                      )}
                    </div>
                  )}
                  
                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-slate-900 mb-1">Errors:</p>
                      <ul className="list-disc ml-4 text-xs text-slate-700 space-y-1">
                        {importResult.errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleImportExcel}
                  disabled={!importFile || importing}
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {importing ? 'Importing...' : 'Import Assets'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowImportModal(false); setImportFile(null); setImportResult(null); }}
                  className="rounded-sm"
                >
                  Close
                </Button>
              </div>

              {/* Required Columns Info */}
              <div className="p-4 bg-slate-50 border border-slate-700 rounded-sm">
                <p className="text-sm font-medium text-slate-900 mb-2">Required Columns:</p>
                <ul className="text-xs text-slate-700 space-y-1">
                  <li>• <strong>asset_id</strong> - Unique identifier (e.g., ASSET-001)</li>
                  <li>• <strong>name</strong> - Name of the asset</li>
                  <li>• <strong>asset_type</strong> - Server, Laptop, Software, etc.</li>
                  <li>• <strong>category</strong> - Infrastructure, Endpoints, etc.</li>
                </ul>
                <p className="text-xs text-slate-600 mt-2">
                  All other columns are optional. See the template for all available fields.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
